import type { ThemePatch } from './types';

/**
 * Theme variants.
 *
 * The theme is the *client's* brand, so an attendee cannot invent one — they
 * choose from a set the client approves in the CMS. `variants` on the bundle is
 * that set. A tenant that ships none gets no picker at all, which is the right
 * default for a client who wants one look and only one look.
 *
 * Each variant is a full ThemePatch, so a look can change ground, surface,
 * text, both ramps, section colours, track rails, corner radius and rule fade —
 * not just the accent. A theme that only swaps the accent does not read as a
 * different app, and that was the point of the request.
 */

export interface ThemeVariant {
  id: string;
  /** Shown on the picker card. */
  label: string;
  /** One line on what the look is for. */
  note: string;
  patch: ThemePatch;
}

/** Neutral ramps, warm and cool, so a variant does not have to spell out nine steps. */
const WARM_NEUTRAL = {
  100: '#fdf6f0', 200: '#f3e6da', 300: '#e0cbb8', 400: '#c2a892', 500: '#a08872',
  600: '#7d6857', 700: '#5c4c40', 800: '#3d332b', 900: '#261f1a',
};
const COOL_NEUTRAL = {
  100: '#f2f6f8', 200: '#dfe7ec', 300: '#c3d0d8', 400: '#9fb0bb', 500: '#7d8f9b',
  600: '#5f717c', 700: '#46555e', 800: '#2f3a41', 900: '#1d2529',
};
const MOSS_NEUTRAL = {
  100: '#f2f6ef', 200: '#e0e8da', 300: '#c6d3bd', 400: '#a5b699', 500: '#849678',
  600: '#66765c', 700: '#4c5a45', 800: '#343f30', 900: '#20281e',
};
const PLUM_NEUTRAL = {
  100: '#3a2b3d', 200: '#4e3c52', 300: '#6b5670', 400: '#8d7592', 500: '#a993ad',
  600: '#c2b1c6', 700: '#d8cddb', 800: '#ebe4ed', 900: '#f7f3f8',
};

/**
 * The built-in set. A client can replace these wholesale in the CMS; these are
 * what Nocturne Con ships with.
 */
export const builtInVariants: ThemeVariant[] = [
  {
    id: 'nocturne',
    label: 'Nocturne',
    note: 'The house look. Deep indigo, violet accent.',
    // Empty patch: this is the tenant's base theme, untouched.
    patch: {},
  },

  {
    id: 'ember',
    label: 'Ember',
    note: 'Warm and low-lit. Copper on near-black, softer corners.',
    patch: {
      colors: {
        bg: '#171310',
        surface: '#241d18',
        text: '#f2e9e1',
        accent: '#d98246',
        accent2: '#c76b4a',
        divider: 'rgba(242,233,225,0.16)',
        neutral: WARM_NEUTRAL,
        accentRamp: {
          100: '#fff6ef', 200: '#ffe6d3', 300: '#fcccab', 400: '#f0a874', 500: '#d98246',
          600: '#b06432', 700: '#874b26', 800: '#5c341b', 900: '#3a2213',
        },
        accent2Ramp: {
          100: '#fff5f1', 200: '#ffe3d9', 300: '#fbc4b3', 400: '#ef9d85', 500: '#c76b4a',
          600: '#a25439', 700: '#7b3f2c', 800: '#552c1f', 900: '#361d15',
        },
        section: '#4a2a18', sectionGlow: '#6b3c1f', sectionGhost: '#8f5330',
        tracks: {
          Panel: '#d98246', Screening: '#a08872', Special: '#f0a874',
          Autographs: '#874b26', Workshop: '#7d6857', Gaming: '#b06432',
        },
      },
      // Rounder corners read as warmer. The type scale is untouched — the
      // system's density is the client's, not the attendee's, to change.
      radius: { sm: 6, md: 12, lg: 20 },
      ruleFade: 64,
    },
  },

  {
    id: 'halogen',
    label: 'Halogen',
    note: 'Cold and technical. Electric cyan, hard edges, high contrast.',
    patch: {
      colors: {
        bg: '#0d1114',
        surface: '#161d22',
        text: '#eaf4f8',
        accent: '#3fd3e8',
        accent2: '#5ba8d4',
        divider: 'rgba(234,244,248,0.18)',
        neutral: COOL_NEUTRAL,
        accentRamp: {
          100: '#f0fdff', 200: '#d3f7fd', 300: '#a5edf8', 400: '#6adeef', 500: '#3fd3e8',
          600: '#2aa3b8', 700: '#1e7a8b', 800: '#155360', 900: '#0e343d',
        },
        accent2Ramp: {
          100: '#f1f8fd', 200: '#d8ecf8', 300: '#b3daf0', 400: '#83bfe2', 500: '#5ba8d4',
          600: '#4082aa', 700: '#2f6180', 800: '#22435a', 900: '#162a38',
        },
        section: '#0f3d46', sectionGlow: '#155b68', sectionGhost: '#1d7d8f',
        tracks: {
          Panel: '#3fd3e8', Screening: '#7d8f9b', Special: '#6adeef',
          Autographs: '#1e7a8b', Workshop: '#5f717c', Gaming: '#4082aa',
        },
      },
      // Near-square corners. The single biggest lever on "technical".
      radius: { sm: 2, md: 3, lg: 4 },
      ruleFade: 24,
    },
  },

  {
    id: 'daybreak',
    label: 'Daybreak',
    note: 'Light mode. Warm paper, deep plum, for a bright exhibit hall.',
    patch: {
      colors: {
        bg: '#f7f4ef',
        surface: '#ffffff',
        text: '#2c2230',
        accent: '#8b4a7d',
        accent2: '#b0648c',
        divider: 'rgba(44,34,48,0.16)',
        // Inverted: step 100 is darkest so "neutral-800 chip on a light ground"
        // still resolves to a readable pair without touching any component.
        neutral: PLUM_NEUTRAL,
        accentRamp: {
          100: '#3d1f36', 200: '#5a2e50', 300: '#77406a', 400: '#8b4a7d', 500: '#a5679a',
          600: '#c08cb6', 700: '#d7b2ce', 800: '#ecd8e6', 900: '#f9f0f6',
        },
        accent2Ramp: {
          100: '#4a2438', 200: '#67354f', 300: '#8b4869', 400: '#b0648c', 500: '#c583a6',
          600: '#d7a4bf', 700: '#e6c4d5', 800: '#f2e0ea', 900: '#fbf4f7',
        },
        section: '#efe4ee', sectionGlow: '#e3d2e4', sectionGhost: '#d4bcd6',
        tracks: {
          Panel: '#8b4a7d', Screening: '#7d6857', Special: '#b0648c',
          Autographs: '#5a2e50', Workshop: '#8d7592', Gaming: '#a5679a',
        },
      },
      radius: { sm: 4, md: 10, lg: 16 },
      ruleFade: 48,
    },
  },

  {
    id: 'verdant',
    label: 'Verdant',
    note: 'Quiet and organic. Forest ground, sage accent, generous edges.',
    patch: {
      colors: {
        bg: '#12180f',
        surface: '#1c2418',
        text: '#e8f0e2',
        accent: '#8fc17a',
        accent2: '#a8b98d',
        divider: 'rgba(232,240,226,0.16)',
        neutral: MOSS_NEUTRAL,
        accentRamp: {
          100: '#f4faf1', 200: '#e2f2da', 300: '#c7e3b9', 400: '#a9d192', 500: '#8fc17a',
          600: '#6d9a5b', 700: '#517444', 800: '#38502f', 900: '#22321d',
        },
        accent2Ramp: {
          100: '#f6f8f1', 200: '#e8eeda', 300: '#d2dfbc', 400: '#bccb9f', 500: '#a8b98d',
          600: '#87976f', 700: '#667254', 800: '#48513b', 900: '#2c3225',
        },
        section: '#1f3a1c', sectionGlow: '#2d5228', sectionGhost: '#3f7137',
        tracks: {
          Panel: '#8fc17a', Screening: '#849678', Special: '#c7e3b9',
          Autographs: '#517444', Workshop: '#66765c', Gaming: '#87976f',
        },
      },
      radius: { sm: 8, md: 16, lg: 24 },
      ruleFade: 72,
    },
  },
];

export function findVariant(
  variants: ThemeVariant[] | undefined,
  id: string | null,
): ThemeVariant | null {
  if (!id || !variants?.length) return null;
  return variants.find((v) => v.id === id) ?? null;
}
